<template>
  <div>
    <div class="row">
      <div class="col-lg-8"></div>
      <div class="col-lg-4 col-12">
        <span> Ship from (Warehouse): </span>
        <v-select
          placeholder="Select Warehouse Location"
          v-model="formData.location_id"
          :options="locations"
          label="text"
          :reduce="text => text.id"
        />
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-12 col-md-2">
        <input type="text" class="form-control quantity-field"
          v-model="barcodeText" 
          id="chat-search"
          placeholder="Scan" aria-label="Search..."
          aria-describedby="chat-search"
          @keydown.enter="onScanBarcode"
          ref="productScanField"
        />
      </div>
      <div class="col-12 col-md-8">
        <AsyncSelect
          placeholder="Select Product"
          v-model="selectedProduct"
          label="text"
          :api-service="fetchProductDropdownList"
          :format-label="option => option.text"
          :option-selected="onSelectNote"
          :additional-query="additionalProductQuery"
          :min-char-to-search="2"
          skip-api-on-open
          input-class="product-field"
          ref="productField"
        />
      </div>
      <div class="col-12 col-md-2">
        <input
          ref="quantityField"
          id="quantity"
          name="quantity"
          type="number"
          class="form-control quantity-field"
          v-model="formData.quantity"
          placeholder="quantity"
          @keydown.enter="handleProductSelection"
        />
      </div>
    </div>

    <AddCustomerModal
      v-if="$store.state.isModalOpenTwo"
      :mobileNotReq="true"
      @onCreateProfile="onCreateProfile"
    />

    <Loader v-if="isLoader"/>

  </div>
</template>


<script setup>
import {computed, inject, onMounted, ref, watch, toRef, nextTick} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useStore} from 'vuex';
import handlePurchase from '@/services/modules/purchase'
import handleBusinessesLocations from '@/services/modules/businessesLocations'
import handleContact from '@/services/modules/contact'
import {useAsyncDropdownHelper} from "@/services/utils/asyncDropdownHelper";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import AddCustomerModal from "@/components/molecule/company/inventory/AddCustomerModal.vue";
import useDate from "@/services/utils/day";
import Loader from "@/components/atom/LoaderComponent";
import handleDropdowns from "@/services/modules/dropdowns";
import useInvoiceCalculations from '@/composables/useInvoiceCalculations.js';

import handleCompanyContact from "@/services/modules/companyContact";

const emit = defineEmits(['finalize']);
const props = defineProps({
  addNewItem: {
    type: Function
  },
  tableItems: {
    type: Array
  },
  additionalProductQuery: {
    type: Object
  },
  salesAccountHeads: {
    type: Array
  },
})

const $route = useRoute();
const $router = useRouter();
const $store = useStore();
const date = useDate();
const customer = ref(null)
const userInfo = ref(null);
const isLoader = ref(false);
const patientSerialNo = ref(null);
const quantityField = ref(null)
const productField = ref(null)
const productScanField = ref(null);
const companyId = $route.params.companyId
const companyQuery = `?company_id=${companyId}`;
const selectedData = ref({});
const formData = inject('formData');
const showError = inject("showError")
const barcodeText = ref(null);
let locations = ref([]);
const selectedProduct = ref(null);
const productId = ref(null);
const tableItemsRef = toRef(props, 'tableItems');

const isFromProductScan = ref(false);
const serialNo = ref(null);
const mobileNo = ref(null);
const additionalQuery = {
  type: "customer",
  age: 1,
  gender: 1
}
const totals = inject('totals');

const {
  total,
  grandTotal,
  discountAmount,
  discountPercent,
  setDiscountAmount,
  resetDiscounts
} = useInvoiceCalculations(tableItemsRef, totals)

const {fetchProductById} = handlePurchase()
const {fetchBusinessLocationsList} = handleBusinessesLocations()
const { fetchContactProfiles, fetchSingleContactProfile } = handleContact()
const {formatPatientLabel} = useAsyncDropdownHelper()
const { fetchProductDropdownList } = handleDropdowns();
const {fetchCompanyAllContacts, fetchContactDetails} = handleCompanyContact();

const patientMobile = computed(() => {
  if (selectedData.value && selectedData.value.profile_items && selectedData.value.profile_items.length) {
    return selectedData.value.profile_items[0].value;
  }
  return null
})

const patientId = computed(() => {
  if (
    selectedData.value &&
    selectedData.value.company_roles &&
    selectedData.value.company_roles.length &&
    selectedData.value.company_roles[0].pivot
  ) {
    return selectedData.value.company_roles[0].pivot.serial_no
  }
  return null
})

const rewardBalance = computed(() => {
  if (selectedData.value && selectedData.value.reward_balance) {
    return selectedData.value.reward_balance;
  }
  return 0;
})

const onSelectNote = (note) => {
  productId.value = note.id
}

const openCustomerAddModal = () => {
    $store.state.isModalOpenTwo = true
}

const onCreateProfile = (profileInfo) => {
  customer.value = profileInfo;
  customer.value.name = profileInfo.full_name
}

const focusProductSelect = () => {
  if (productField.value) {
    productField.value.clear();
    productField.value.dropdownRef.searchEl.focus()
  }
}

const focusProductScan = () => {
  if (productScanField.value) {
    productScanField.value.focus();
  }
  isFromProductScan.value = false;
}

const focusMethod = () => {
  focusProductSelect();
  if (isFromProductScan.value) {
    focusProductScan();
  }
}

const handleProductSelection = async () => {
  if (!productId.value) {
    showError("Please select product first!");
    focusMethod();
    return;
  }

  if (props.tableItems.length > 0) {
    emit('finalize');
    focusMethod();
    return;
  }

  if (!formData.value.quantity) {
    showError("Please enter valid quantity first!");
    return;
  }

  let quantity = Number(formData.value.quantity)

  if (!Number.isInteger(quantity)) {
    showError("The quantity can only be a number");
    formData.value.quantity = parseInt(formData.value.quantity)
    return;
  }

  const productResp = await fetchProductById(productId.value, `${companyQuery}&location_id=${formData.value.location_id}`);

  if (!productResp.data) {
    showError("Product not found");
    focusMethod();
    return;
  }

  const selected = productResp.data;
  let alreadyExist = false;

  let product = {
    name: selected.name
  }

  props.tableItems.map((item) => {
    if (item.product_id === selected.id) {
      alreadyExist = true;
      item.quantity += quantity;
    }
  })

  if (!alreadyExist) {
    props.tableItems.push({
      product_id: selected.id,
      name: selected.name,
      stock_in_hand: selected.stock_in_hand,
      description: '',
      quantity: quantity,
      rate: selected.sales_price,
      purchase_rate: selected.purchase_rate,
      discount: 0,
      vat: 0,
      sub_total: 0,
      discount_amount: 0,
      discount_percent: 0,
      vat_amount: 0,
      total: 0,
      is_edit: false,
      batch_details: [],
      product: product
    })
  }

  formData.value.quantity = null;
  productId.value = null;
  selectedProduct.value = null;
  focusMethod();
}
const generateDateAndInvoice = () => {
  formData.value.date = date.currentDate();
}

const resetData = async () => {
  generateDateAndInvoice();
  customer.value = null;
  productId.value = null;
  patientSerialNo.value = null;
  formData.value.location_id = locations.value[0].id
  tableItemsRef.value = [];
  productField.value.clear();
  barcodeText.value = null;

  resetDiscounts();
  await getInitialContact();
  focusProductScan();
}

const onScanBarcode = async () => {
  if (! barcodeText.value) return;
  if (props.tableItems.length > 0) {
      emit('finalize');
      return;
  }

  let query = `${companyQuery}&with_desc_stock=1&barcode_sku=${barcodeText.value}&location_id=${formData.value.location_id}`;

  try {
    isLoader.value = true;
    const response = await fetchProductDropdownList(query);

    if (! response.status) {
      showError("No product found with this barcode");
      barcodeText.value = null;
      isLoader.value = false;
      return;
    }

    const foundProduct = response.data[0];
    productId.value = foundProduct.id;
    selectedProduct.value = foundProduct;
    barcodeText.value = null;
    formData.value.quantity = 1;
    isFromProductScan.value = true;
    await handleProductSelection();
  } catch (error) {
    console.log(error)
    showError("Failed to fetch product by barcode");
  } finally {
    isLoader.value = false;
  }
}

const getInitialContact = async () => {
  const contactQuery = companyQuery + '&type=customer&age=1&gender=1&fetch_one_only=1';
  const res = await fetchContactProfiles(contactQuery);
  if (res.data && res.data.length) {
    customer.value = res.data[0];
    selectedData.value = formData.value.contact = res.data[0];
  }
}

const searchContact = async () => {
  if (!mobileNo.value && !serialNo.value) {
    showError("Enter Mobile No or Serial No first");
    return;
  }

  isLoader.value = true;
  
  let query = companyQuery + '&roles=customer';

  if (mobileNo.value) query += `&q=${mobileNo.value}`;
  if (serialNo.value) query += `&serial_no=${serialNo.value}`;

  try {
    const res = await fetchCompanyAllContacts(query);

    if (! res.status) {
      showError("No customer found");
      return;
    }

    if(res.data && res.data.data.length) {
      customer.value = {
        id: res.data.data[0].id,
        name: res.data.data[0].full_name
      };
      formData.value.contact_profile_id = res.data.data[0].id;
    }

    mobileNo.value = null;
    serialNo.value = null;
  } catch (e) {
    console.log(e);
    showError("Failed to search customer");
  } finally {
    isLoader.value = false;
  }
};

onMounted(async () => {
  generateDateAndInvoice();
  const locationQuery = `${companyQuery}&location_type=fg_warehouse`;
  fetchBusinessLocationsList(locationQuery).then(res => {
    if (res.data) {
      locations.value = res.data
      formData.value.location_id = res.data[0].id
    }
  });
  await getInitialContact();
  nextTick(() => {
    focusProductScan();
  });
})



watch(customer, () => {
  const patientQuery = companyQuery + '&type=customer&reward_balance=1';
  formData.value.contact_profile_id = null;
  selectedData.value = null;
  if (!customer.value) {
    return
  }
  formData.value.contact_profile_id = customer.value.id;
  fetchSingleContactProfile(customer.value.id, patientQuery).then(res => {
    if (res.data) {
      selectedData.value = formData.value.contact = res.data;
      rewardBalance.value = res.data.reward_balance;
    }
  })
})

watch(productId, () => {
  if (!productId.value) {
    return;
  }

  if (quantityField.value) {
    quantityField.value.focus();
  }
})

watch(discountAmount, (newValue) => {
  if(newValue > 0 && props.salesAccountHeads.length > 0) {
    formData.value.discount_head_id = props.salesAccountHeads[0].id;
    formData.value.adjustment_amount = discountAmount.value;
  }
})

watch(mobileNo, (val) => {
  if (val) serialNo.value = null;
})

watch(serialNo, (val) => {
  if (val) mobileNo.value = null;
})

defineExpose({
  resetData
})

</script>

<style scoped>
.font-20 {
  font-size: 20px;
}
</style>